import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Box, Checkbox, FormControlLabel } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { AssetSelect, CompanySelect, CreatePage, FuseInput, LoanSelect } from '/src/components';
import { useAssetStore, useFilterStore, usePaymentStore } from '/src/context';
import { toastMessages } from '/src/lib/messages';
import { TAssetId, TCompanyId, TLoanId } from '/src/lib/models';
import { AssetStatus, CreatePaymentForm, DataModelType, FormErrors } from '/src/lib/types';
import * as utils from '/src/utils';

/**
 * Create Payment Page
 */
export const CreatePaymentPage = observer(() => {
  /** Context **/
  const assetStore = useAssetStore();
  const paymentStore = usePaymentStore();
  const filterStore = useFilterStore();
  const navigate = useNavigate();

  /** State **/
  const [payoff, setPayoff] = useState(false);

  const [formValues, setFormValues] = useState<CreatePaymentForm>({
    name: '',
    company: undefined,
    loan: undefined,
    asset: undefined,
    amount: 0,
    paymentType: '',
    datePaid: '',
    dateDeposited: '',
    effectiveDate: '',
    notes: [],
    files: [],
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  /** Computed **/
  const isEstimating = paymentStore.isEstimating;
  const payoffEstimate = paymentStore.payoffEstimate;

  const companies = useMemo(
    () => filterStore.companyListGet().filter((company) => company.loans?.length),
    [filterStore._companyList]
  );

  const loans = useMemo(
    () =>
      filterStore.loanListGet().filter((loan) => loan.assets.length && loan.borrower._id === formValues.company?._id),
    [filterStore._loanList, formValues.company]
  );

  const assets = useMemo(
    () =>
      formValues.loan?.assets
        ?.filter((asset) => asset.status === AssetStatus.Open)
        .sort((a, b) => (a.name > b.name ? -1 : 1)) ?? [],
    [formValues.loan, formValues.company]
  );

  /** Methods **/

  /**
   * Updates a form value. Designed so that, in a form field's `onChange` method,
   * you pass this function with the name of the prop you want to update, and it
   * returns a function that will automatically handle saving the returned value.
   *
   * @param key Name of the form prop to update.
   */
  const updateFormValue = (key: string) => (value: any) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
    if (formErrors[key]) {
      setFormErrors((prev) => ({
        ...prev,
        [key]: '',
      }));
    }
  };

  /**
   * Validates the form data. If errors are detected, it saves them to `formErrors` and returns false.
   *
   * @returns Boolean indicating whether the data is valid
   */
  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    const errorMessages = {
      company: 'Company is required',
      loan: 'Loan is required',
      asset: 'Asset is required',
      name: 'Payment Name is required',
      amount: 'Amount is required',
      paymentType: 'Payment Type is required',
      datePaid: 'Date Paid is required',
      dateDeposited: 'Date Deposited is required',
    };

    if (!formValues.company) errors.company = errorMessages.company;
    if (!formValues.loan) errors.loan = errorMessages.loan;
    if (!formValues.asset) errors.asset = errorMessages.asset;
    if (!formValues.name) errors.name = errorMessages.name;
    if (!utils.isNumber(formValues.amount) || !formValues.amount) errors.amount = errorMessages.amount;
    if (!formValues.paymentType) errors.paymentType = errorMessages.paymentType;
    if (!formValues.datePaid) errors.datePaid = errorMessages.datePaid;
    if (!formValues.dateDeposited) errors.dateDeposited = errorMessages.dateDeposited;

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const clearSavedEstimate = () => {
    paymentStore.payoffEstimate = undefined;
    updateFormValue('dateDeposited')(undefined);
  };

  const clearAsset = () => {
    updateFormValue('asset')(undefined);
  };

  const changeCompany = (companyId: TCompanyId) => {
    clearSavedEstimate();
    clearAsset();
    updateFormValue('company')(companies.find((company) => company._id === companyId));
  };

  const changeLoan = (loanId: TLoanId) => {
    clearSavedEstimate();
    clearAsset();
    updateFormValue('loan')(loans.find((loan) => loan._id === loanId));
  };

  const changeAsset = async (assetId: TAssetId) => {
    const response = await assetStore.get(assetId);

    if (response) {
      updateFormValue('asset')({
        ...assets.find((asset) => asset._id === assetId),
        expectedResidualPayment: response.expectedResidualPayment,
        currentPayments: response.currentPayments,
        totalDueSocket: response.totalDueSocket,
      });
    }
  };

  /**
   * Creates a Payment.
   *
   * @remarks
   * Redirects to the new Payment's Detail page afterwards.
   */
  const create = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors first');
      return;
    }

    try {
      const response = await paymentStore.create(formValues);

      toast.success(toastMessages.createPayment.success);
      navigate(`/payments/${response._id}`);
    } catch (err) {
      console.error(err);
      toast.error(toastMessages.createPayment.error);
    }
  };

  /** Effects **/

  useEffect(() => {
    if (payoff && formValues.dateDeposited && formValues.loan && formValues.asset) {
      paymentStore.estimatePayoff(formValues.loan._id, new Date(formValues.dateDeposited));
    }
  }, [payoff, formValues.dateDeposited, formValues.loan, formValues.asset]);

  /** Render **/
  return (
    <CreatePage modelType={DataModelType.Payment} onCreate={create} createVerb="Record" width="100%">
      {/* Company */}
      <CompanySelect
        value={formValues.company?._id ?? ''}
        setValue={changeCompany}
        companies={companies}
        error={formErrors.company}
      />

      {/* Loan */}
      <LoanSelect value={formValues.loan?._id ?? ''} setValue={changeLoan} loans={loans} error={formErrors.loan} />

      {/* Asset */}
      <AssetSelect
        value={formValues.asset?._id ?? ''}
        setValue={changeAsset}
        assets={assets}
        error={formErrors.asset}
      />

      <FuseInput
        label="Payment Name"
        field="name"
        formValues={formValues}
        updateValue={updateFormValue('name')}
        required
        error={formErrors.name}
      />
      <Box display="flex" justifyContent="center" alignItems="center">
        <Box display="flex" flexDirection="column" flex={50}>
          <Box>
            <Box display="flex">
              {/* Payoff Estimate */}
              <Box
                display="flex"
                alignItems="center"
                className="padding-25 textZoomIn"
                height="100%"
                flex={1}
                gap="16px"
              >
                {/* Payoff Deal */}
                <Box display="flex" justifyContent="center" alignItems="center">
                  <FormControlLabel
                    label="Payoff Deal"
                    sx={{ color: 'rgba(0, 0, 0, 0.87)' }}
                    control={<Checkbox checked={payoff} onChange={(e) => setPayoff(e.target.checked)} name="payoff" />}
                  />
                </Box>

                {payoff && (
                  <Box
                    flex={80}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    flexDirection="column"
                    className="md-title"
                    height="100%"
                  >
                    <Box
                      paddingBottom="6px"
                      marginBottom="6px"
                      borderBottom="solid 1px rgba(0,0,0,0.12)"
                      className="thin"
                    >
                      Payoff Estimate
                    </Box>
                    {!isEstimating && payoffEstimate && (
                      <Box id="payoffEstimate">{utils.currency.format(payoffEstimate)}</Box>
                    )}
                    {!isEstimating && payoff && !formValues.dateDeposited && !formValues.asset && !formValues.loan && (
                      <Box className="md-caption center" fontWeight="100">
                        <strong>Please choose a Loan.</strong>
                      </Box>
                    )}
                    {!isEstimating && payoff && !formValues.dateDeposited && !formValues.asset && formValues.loan && (
                      <Box className="md-caption center" fontWeight="100">
                        <strong>Please choose an Asset.</strong>
                      </Box>
                    )}
                    {!isEstimating && payoff && !formValues.dateDeposited && formValues.asset && formValues.loan && (
                      <Box className="md-caption center" fontWeight="100">
                        <strong>No date deposited set.</strong>
                      </Box>
                    )}
                    {/* {formValues.dateDeposited && !payoffEstimate && ( */}
                    {isEstimating && (
                      <Box className="md-caption center" fontWeight="100">
                        <strong>Estimating...</strong>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Box>

            <Box display="flex" flex={25}>
              <FuseInput
                label="Amount"
                field="amount"
                formValues={formValues}
                updateValue={updateFormValue('amount')}
                min="0"
                moneyMask
                required
                error={formErrors.amount}
              />
            </Box>
          </Box>
          <FuseInput
            label="Payment Type"
            field="paymentType"
            formValues={formValues}
            updateValue={updateFormValue('paymentType')}
            required
            error={formErrors.paymentType}
          />
        </Box>

        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          className="md-title thin"
          height="100%"
          flexDirection="column"
          gap="32px"
          flex={50}
        >
          <Box display="flex" width="100%">
            <Box
              flex={50}
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
              className="md-title"
              height="100%"
            >
              <Box paddingBottom="6px" marginBottom="6px" borderBottom="solid 1px rgba(0,0,0,0.12)" className="thin">
                Payments To Date
              </Box>
              {formValues.asset && (
                <Box id="currentPayments">{utils.currency.format(formValues.asset?.currentPayments ?? 0)}</Box>
              )}
              {!formValues.asset && (
                <Box className="md-caption center" fontWeight="100">
                  <strong>Please select an Asset.</strong>
                </Box>
              )}
            </Box>

            <Box
              flex={50}
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
              className="md-title"
              height="100%"
            >
              <Box paddingBottom="6px" marginBottom="6px" borderBottom="solid 1px rgba(0,0,0,0.12)" className="thin">
                Total Due Socket
              </Box>
              {formValues.asset && formValues.loan?.isOwned && (
                <Box id="totalDueSocket">{utils.currency.format(formValues.asset.totalDueSocket || 0)}</Box>
              )}
              {(!formValues.asset || !formValues.loan?.isOwned) && (
                <Box className="md-caption center" fontWeight="100">
                  <strong>Please select an "Owned" Asset.</strong>
                </Box>
              )}
            </Box>
          </Box>

          <Box display="flex" width="100%">
            <Box
              flex={50}
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
              className="md-title"
              height="100%"
            >
              <Box paddingBottom="6px" marginBottom="6px" borderBottom="solid 1px rgba(0,0,0,0.12)" className="thin">
                Initial Payment Amount
              </Box>
              {formValues.asset && (
                <Box id="initialPaymentAmount">{utils.currency.format(formValues.asset.initialPaymentAmount || 0)}</Box>
              )}
              {!formValues.asset && (
                <Box className="md-caption center" fontWeight="100">
                  <strong>Please select an Asset.</strong>
                </Box>
              )}
            </Box>

            <Box
              flex={50}
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
              className="md-title"
              height="100%"
            >
              <Box paddingBottom="6px" marginBottom="6px" borderBottom="solid 1px rgba(0,0,0,0.12)" className="thin">
                Expected Residual Payment
              </Box>
              {formValues.asset && (
                <Box id="expectedResidualPayment">
                  {utils.currency.format(formValues.asset.expectedResidualPayment || 0)}
                </Box>
              )}
              {!formValues.asset && (
                <Box className="md-caption center" fontWeight="100">
                  <strong>Please select an Asset.</strong>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Box display="flex" justifyContent="center" alignItems="center" gap="16px">
        <FuseInput
          label="Date Paid"
          field="datePaid"
          formValues={formValues}
          updateValue={updateFormValue('datePaid')}
          type="date"
          required
          boxProps={{ className: 'pad-bottom-xs' }}
          error={formErrors.datePaid}
        />
        <FuseInput
          label="Date Deposited"
          field="dateDeposited"
          formValues={formValues}
          updateValue={updateFormValue('dateDeposited')}
          type="date"
          required
          boxProps={{ className: 'pad-bottom-xs' }}
          error={formErrors.dateDeposited}
        />
      </Box>
    </CreatePage>
  );
});
